import React, { useState, useEffect } from 'react';
import { X, MapPin, Package, Clock, User, Warehouse } from 'lucide-react';
import { putAwayService } from '../../../../services/putAwayService';
import { StcIntRelOrderDetailsDto } from '../../../../types/putaway';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { StatusBadge } from '../../../common/StatusBadge';

interface PreviewPutAwayModalProps {
  isOpen: boolean;
  stcOrderNo: number | null;
  onClose: () => void;
}

export const PreviewPutAwayModal: React.FC<PreviewPutAwayModalProps> = ({
  isOpen,
  stcOrderNo,
  onClose
}) => {
  const { t } = useTranslation();
  const [orderDetails, setOrderDetails] = useState<StcIntRelOrderDetailsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && stcOrderNo) {
      loadOrderDetails();
    }
  }, [isOpen, stcOrderNo]);

  const loadOrderDetails = async () => {
    if (!stcOrderNo) return;

    try {
      setLoading(true);
      setError(null);
      const details = await putAwayService.getPutAwayOrderDetails(stcOrderNo);
      setOrderDetails(details);
    } catch (err) {
      const error = err as Error;
      setError(error.message);
      console.error('Error loading put-away order details:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: number) => {
    switch (status) {
      case 0: return 'bg-gray-100 text-gray-800';     // Ny
      case 10: return 'bg-green-100 text-green-800';   // Tildelt
      case 20: return 'bg-blue-100 text-blue-800';     // Under arbeid
      case 30: return 'bg-yellow-100 text-yellow-800'; // Pauset
      case 100: return 'bg-green-100 text-green-800';  // Ferdig
      case 110: return 'bg-red-100 text-red-800';      // Avbrutt
      default: return 'bg-gray-100 text-gray-800';     // Default
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('no-NO');
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg shadow-xl max-w-4xl sm:w-full sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-green-600 text-white p-3 sm:p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <MapPin size={20} className="sm:w-6 sm:h-6 flex-shrink-0" />
            <h2 className="text-base sm:text-lg font-semibold truncate">
              <span className="hidden sm:inline">{t('warehouse.modules.putaway.preview.title')} #{stcOrderNo}</span>
              <span className="sm:hidden">{t('warehouse.modules.putaway.preview.orderShort')} #{stcOrderNo}</span>
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0"
          >
            <X size={20} className="sm:w-6 sm:h-6" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {loading && (
            <div className="flex items-center justify-center py-8 sm:py-12 px-4">
              <div className="w-6 h-6 sm:w-8 sm:h-8 animate-spin border-2 border-green-500 border-t-transparent rounded-full" />
              <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-600">{t('warehouse.modules.putaway.messages.loadingDetails')}</span>
            </div>
          )}

          {error && (
            <div className="p-4 sm:p-6 text-center text-red-600">
              <p className="text-sm sm:text-base">{t('warehouse.modules.putaway.errors.loadingDetailsFailed')}: {error}</p>
            </div>
          )}

          {orderDetails && (
            <div className="p-4 sm:p-6">
              {/* Order Header Info */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div className="flex items-center gap-2">
                    {/* Ordrestatus = StatusType 26 (master-basert, språk-bevisst) */}
                    <StatusBadge value={orderDetails.header.orderStatus} statusType={26} />
                  </div>

                  <div className="flex items-center gap-2 text-gray-600">
                    <Package size={16} />
                    {/* TransType = StatusType 25 (Utlegg/Påfylling/Optimalisering) */}
                    <StatusBadge value={orderDetails.header.transType} statusType={25} />
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-600">
                    <Warehouse size={16} />
                    <span className="text-sm">{t('warehouse.general.warehouse')}: {orderDetails.header.warehouseID}</span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock size={16} />
                    <span className="text-sm">{formatDate(orderDetails.header.transDt)}</span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-600">
                    <User size={16} />
                    <span className="text-sm">
                      {orderDetails.header.lockedBy ? `${t('warehouse.modules.putaway.labels.locked')}: ${orderDetails.header.lockedBy}` : t('warehouse.modules.putaway.labels.available')}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-gray-600">
                    <Package size={16} />
                    <span className="text-sm">{t('warehouse.modules.putaway.labels.priority')}: {orderDetails.header.orderPriority}</span>
                  </div>
                </div>

                {orderDetails.header.description1 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-700 font-medium">
                      {orderDetails.header.description1}
                    </p>
                  </div>
                )}
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
                <div className="bg-blue-50 p-2 sm:p-3 rounded-lg text-center">
                  <div className="text-xl sm:text-2xl font-bold text-blue-600">{orderDetails.totalLines}</div>
                  <div className="text-xs text-blue-700">{t('warehouse.modules.putaway.stats.totalLines')}</div>
                </div>
                <div className="bg-green-50 p-2 sm:p-3 rounded-lg text-center">
                  <div className="text-xl sm:text-2xl font-bold text-green-600">{orderDetails.completedLines}</div>
                  <div className="text-xs text-green-700">{t('warehouse.modules.putaway.labels.completed')}</div>
                </div>
                <div className="bg-purple-50 p-2 sm:p-3 rounded-lg text-center">
                  <div className="text-xl sm:text-2xl font-bold text-purple-600">{orderDetails.totalQuantity}</div>
                  <div className="text-xs text-purple-700">{t('warehouse.modules.putaway.stats.totalQuantity')}</div>
                </div>
                <div className="bg-orange-50 p-2 sm:p-3 rounded-lg text-center">
                  <div className="text-xl sm:text-2xl font-bold text-orange-600">{Math.round(orderDetails.completionPercentage)}%</div>
                  <div className="text-xs text-orange-700">{t('warehouse.modules.putaway.stats.completed')}</div>
                </div>
              </div>

              {/* Order Lines */}
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  {t('warehouse.modules.putaway.labels.putawayLines')} ({orderDetails.lines.length})
                </h3>

                <div className="space-y-2 sm:space-y-3">
                  {orderDetails.lines.map((line) => (
                    <div key={line.stcLineNo} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-sm transition-shadow">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {t('warehouse.modules.putaway.labels.line')} {line.stcLineNo}: {line.itemNo}
                            </span>
                            <div className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(line.lineStatus)}`}>
                              {line.statusText}
                            </div>
                          </div>
                          
                          <div className="text-sm text-gray-600 mb-2">
                            {line.description1 || t('common.labels.noDescription')}
                          </div>

                          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                            {line.fromBinCode && (
                              <span>{t('warehouse.modules.putaway.labels.from')}: {line.fromBinCode}</span>
                            )}
                            {line.toBinCode && (
                              <span>{t('warehouse.modules.putaway.labels.to')}: {line.toBinCode}</span>
                            )}
                            {line.grShipmentNo && (
                              <span>{t('warehouse.modules.putaway.labels.shipment')}: {line.grShipmentNo}</span>
                            )}
                          </div>
                        </div>

                        <div className="text-right whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {line.quantityMoved || 0} / {line.quantity}
                          </div>
                          <div className="text-xs text-gray-500">
                            {t('warehouse.modules.putaway.labels.remaining')}: {line.remainingQuantity}
                          </div>
                          {line.movedBy && (
                            <div className="text-xs text-gray-500">
                              {t('warehouse.modules.putaway.labels.movedBy')}: {line.movedBy}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {orderDetails.lines.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Package size={48} className="mx-auto mb-4 text-gray-300" />
                    <p>{t('warehouse.modules.putaway.messages.noLinesFound')}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 sm:px-6 py-3 flex justify-end flex-shrink-0 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors min-h-[44px] text-sm sm:text-base"
          >
            {t('common.buttons.close')}
          </button>
        </div>
      </div>
    </div>
  );
};
